
import Vue from 'vue'

import { TweenLite, Elastic, Power2 } from 'gsap'
import $ from 'domtastic'
import { CustomEase } from '../../utils/CustomEase.js'
import { isMobile } from 'mobile-device-detect'
import { CSS2RGB } from '@/common/utils/colorUtils'
import { ProjectState } from '../../channel.types'
import { getStore } from '../../store/store'
import injectCSS from '@/common/utils/injectCSS'

require('@/common/assets/alex-core-layout-flexbox.css')

/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//
//  Example:
//
//  <hotspot id="paragraph_hotspot" type="emoji" icons="lumi_love,lumi_shocked" trigger="click">
//
//  id. string. required.
//     the html id property for the outermost element of the hotspot. NOTE, when you use an id, you can also then use a css-class of that
//     same name, and that css class will then target the ui element that pops up over the highlight.
//
//  type. string. required.
//     the type of hotspot. right now "emoji" is the only one.
//
//  icons. string. required.
//     A CSV list of emoticons. See here: https://app.lumiere.is/Static/emoji.html
//
//  class. string. optional.
//    This is another class that will be applied to the outermost element. You can target the highlight this way.
//
//  trigger. string. optional.
//    Either "click" or "hover". This determines how this ui element is triggered. The default is "hover"
//
//  Custom CSS: Use the channel-wide CSS overrides in the lumiere-admin. Use the id of a particular element as the CSS selector.
//
//    #paragraph_hotspot {
//      background:#000066;
//      color:white;
//      border-radius:5px;
//    }
//
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// the show/hideEmojis functions take care of the states.
//
// Five states to manage the mouseover-mouseout animation.
// 0: rest-state, animated-out. The initial state. No animations are in motion, nor are we waiting for one to start.
// 1: animate-in. The system is currently animating in.
// 2: animate-out. The system is currently animating out.
// 3. wait. The system is waiting for 3 seconds before switching to state 2 and animating-out.
// 4. cancel. The system was in the wait state (3) but then the user rolled back over the target.
// 5. rest-state. animated-in. The emojis are displayed. No animations are in motion nor are we waiting for one to start.
// 6. engagement. The user clicked/touched an emoji, and the emojis are animating out. We have a special state for this
//        because we don't want the system to respond to events during this animation.
//
//Ok, so the emojis are visible in states 1,2,3,4,5,6, and invisible in 0.

enum Triggers {
  Hover = 'hover',
  Click = 'click',
}

export default Vue.extend({
  props: {
    id: String,
    icons: String,
    trigger: {
      default: 'hover',
      type: String,
      validator: val => Object.values(Triggers).includes(val),
    },
  },

  data: () => {
    return {
      isThisAHeroActivity: false,
      heroEmojis: null,
      idForHeroActivity: null,
      positionOfMatch: 1,
      percentPositionOfMatch: 1,
      lengthOfInput: 1,
      store: getStore(),
      waitTimer: null,
      showEmojisTouchWait: 3000,
      hideEmojisMouseWait: 1000,
      emojisAnimationState: 0,
      lastCalled: '',
      blockedAnimation: 0,
      pctPaddingH: 20,
      pctPaddingV: 35,
      backgroundAlphaDefault: '0.5',
      backgroundColorDefault: '#3299a8',
      borderRadiusDefault: '0px',
      paddingDefault: '0.15em',
      customEase: CustomEase.create(
        'custom',
        'M0,0 C0,0 0.049,0.675 0.085,1.115 0.122,1.498 0.342,1.336 0.41,1.216 0.504,1.07 0.46,0.886 0.612,0.886 0.663,0.886 0.736,0.951 0.82,0.98 0.905,1.008 1,1 1,1'
      ),
    }
  },

  async mounted() {
    try {
      //The dynamism with v-runtime-template is a real barrier. We have a heck of a time passing other objects and properties
      //into hotspot instances that aren't enumerated in the tags. The Vuex store gets in here by means of an import, and we
      //had to write a new module for that as well as re-configure the channel.main.js. At any rate, for hero-articles, there's
      //not just ONE of them, there are several, and they all exist outside of this system of state.selection.activity.
      //That means that we cant just go to the article_engagements section of vuex and see what the current emojis.list
      //BECAUSE EACH HERO ARTICLE HAS ITS OWN. So we store all of the emojis-lists for ActivityHeros in a spot on vuex, and key
      //each of them to the activitiy.id. Then right here, we get the idForHeroActivity from the parent levels, and then
      //we attempt to reacquire thos emojis lists here. Dynamic components are a real headache in Vue.
      let idForHeroActivity = this.$parent.$parent.idForHeroActivity
      this.idForHeroActivity = idForHeroActivity

      if (idForHeroActivity || idForHeroActivity === 0) {
        let id = idForHeroActivity + ''

        if (this.store.state.hero_article_states[id]) {
          this.isThisAHeroActivity = true
          this.heroEmojis = this.store.state.hero_article_states[id]['emojis']
        } else {
          this.isThisAHeroActivity = false
        }
      }
    } catch (error) {
      console.log(error)
    }
  },

  computed: {
    uiClasses() {
      const hasAdditional = !!this.id
      let classes = ['al-abox']
      if (hasAdditional) {
        classes.push(this.id)
      }
      return classes
    },
    //Easy to incorporate the trigger- this thing is already set up to work with touches/clicks
    touchBasedUI() {
      return isMobile || this.trigger == 'click'
    },
    activeEmojiId() {
      return this.store.state.article_engagements.emojis.active_emoji_id
    },

    //We only want one emoji-hotspot at a time displaying emojis.
    //So if the activeEmojiId is null, then it's fair-game for all. If it's not null,
    //then it has to match this id in order for it to be free to present.

    isFreeToPresent() {
      let eId = this.activeEmojiId
      return eId == this.id || eId == null
    },

    loading() {
      if (this.isThisAHeroActivity) {
        return false
      } else {
        return this.store.state.article_engagements.article_loading
      }
    },

    projectCustomCSS() {
      return this.store.state.project.custom_css
    },

    //with hero activities, we don't have the luxury of just going to vuex and asking the article_engagements section,
    //because no single hero activity is ever activated and therefore a single emoji list from the article_engagements simply
    //does not exist.
    emojisForActivity() {
      return this.isThisAHeroActivity
        ? this.heroEmojis
        : this.store.state.article_engagements.emojis.list
    },

    emojisList() {
      let emojisCSV = this.icons

      try {
        if (emojisCSV) {
          let split = emojisCSV.split(',')
          let emojisList = []

          for (let i = 0; i < split.length; i++) {
            let dirty = split[i]
            let clean = dirty.trim()
            let emojiInfo = this.getEmojiDataWithName(clean)
            if (emojiInfo) {
              emojisList.push(emojiInfo)
            }
          }

          return emojisList
        } else {
          return this.emojisForActivity
        }
      } catch (error) {
        console.log(error)
      }
    },

    primaryColor() {
      return this.store.state.project.primary_color
    },
    defaultBackgroundColor() {
      let color = this.primaryColor || this.backgroundColorDefault
      let alpha = this.backgroundAlphaDefault
      let rgb: any = CSS2RGB(color)
      let backgroundColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
      return backgroundColor
    },

    defaultTextColor() {
      return this.store.state.project.contrast_color
    },
  },

  watch: {
    emojisAnimationState(newVal, oldVal) {
      //this means that the emoji moved from visible presentation states to  the invisible fully animated-out state
      if (newVal == 0 && oldVal != 0) {
        if (this.id == this.activeEmojiId) {
          this.store.commit('set_active_emoji_id', null)
        }
      }
    },

    //if it's not free-to-present, then zap it back to its originai animated-out-state.
    isFreeToPresent(newVal, oldVal) {
      if (newVal == false) {
        this.blockedAnimation = 0
        this.hideEmojisAnimate(true)
        this.emojisAnimationState = 0
      }
    },
  },

  methods: {
    //this will return objects like this:
    //{"emoji":"lumi_angry","url":"https://s3.amazonaws.com/media.app.lumiere.is/app/images/icons/lumi_angry.svg"}
    getEmojiDataWithName(name) {
      let emojis = this.emojisForActivity

      for (let i = 0; i < emojis.length; i++) {
        let emojiName = emojis[i].emoji
        if (emojiName == name) {
          return emojis[i]
        }
      }

      return null
    },

    handleClick(event) {
      //for hero-activities. We don't want the click to be interpreted as an opportunity to start the activity.
      event.stopPropagation()
      event.preventDefault()

      //only proceed if we're in the rest or the waiting state.
      if (this.emojisAnimationState == 1 || this.emojisAnimationState == 2) {
        return
      }

      //if the user clicked an emoji icon we're going to record that engagement
      //and animate-out the UI.
      if (event.target && event.target.getAttribute('data-emoji')) {
        let emojiIndex = event.target.getAttribute('data-emoji-index')
        this.recordEmojiEngagement(emojiIndex, event.target)

        //the engagement was performed, so we're going to animate-out the
        //UI. We reset the states and call the animation. For mouse scenarios
        //we're going to stop the timer as well.
        if (this.touchBasedUI) {
          this.emojisAnimationState = 6
          this.hideEmojisAnimate()
        } else {
          this.lastCalled = 'show'
          this.emojisAnimationState = 6
          if (this.waitTimer) {
            clearTimeout(this.waitTimer)
            this.waitTimer = null
          }
          this.hideEmojisAnimate()
        }
      } else {
        if (this.touchBasedUI) {
          this.showEmojisTouch()
        }
      }
    },

    recordEmojiEngagement(emojiIndex, emojiImg) {
      let engagementInfo = this.emojisList[emojiIndex]
      let emojiBox = emojiImg.parentNode

      let emojiBoxNew = document.createElement('div')
      emojiBoxNew.style.transform = emojiBox.style.transform
      emojiBoxNew.setAttribute('class', 'al-abox emoji-box')

      let emojiImageNew = document.createElement('img')
      emojiImageNew.setAttribute('src', engagementInfo['url'])
      emojiImageNew.setAttribute('name', engagementInfo['emoji'])

      emojiImageNew.setAttribute('class', 'emoji')

      emojiBoxNew.appendChild(emojiImageNew)

      this.$refs.emojisEngagementContainer.appendChild(emojiBoxNew)

      if (this.isThisAHeroActivity) {
        this.store.dispatch('article_interaction_hero_activity', {
          hotspotId: this.id,
          hotspotValue: engagementInfo['emoji'],
          id: this.idForHeroActivity,
        })
      } else {
        this.store.dispatch('article_interaction', {
          hotspotId: this.id,
          hotspotValue: engagementInfo['emoji'],
        })
      }

      TweenLite.to(emojiBoxNew, 0.5, {
        scale: 2,
        opacity: 0,
        onComplete: () => {
          this.$refs.emojisEngagementContainer.removeChild(emojiBoxNew)
        },
      })
    },

    showEmojisTouch(event) {
      //bail out if we're in a touch-based environment
      if (!this.touchBasedUI) {
        return
      }

      this.store.commit('set_active_emoji_id', this.id)

      if (this.waitTimer) {
        clearTimeout(this.waitTimer)
        this.waitTimer = null
      }

      this.waitTimer = setTimeout(() => {
        this.emojisAnimationState = 2
        this.hideEmojisAnimate()
      }, this.showEmojisTouchWait)

      if (this.emojisAnimationState == 0) {
        this.emojisAnimationState = 1
        this.showEmojisAnimate()
      }
    },

    showEmojisMouse(event) {
      //bail out if we're in a touch-based environment
      if (this.touchBasedUI) {
        return
      }

      this.store.commit('set_active_emoji_id', this.id)

      //if the emojis are animating out after an engagement then take this early exit.
      if (this.emojisAnimationState == 6) {
        return
      }

      //If we're in the animate-out state, then we need to queue this animation so that it will
      //take place after the animate-out is finished. And then we go no further.
      //You'll notice that we admit the wait-state here and the function will not take the early exit.
      if (this.emojisAnimationState == 2) {
        this.blockedAnimation = 1
        return
      }

      this.lastCalled = 'show'

      //if we're in a rest-state, then set us to the animate-in state
      //and start the animation.
      if (this.emojisAnimationState == 0) {
        this.emojisAnimationState = 1
        this.blockedAnimation = 0
        this.showEmojisAnimate()
      }

      //else if we're in a wait-state, then set us to the cancel-state.
      else if (this.emojisAnimationState == 3) {
        this.emojisAnimationState = 4
      }
    },

    async hideEmojisMouse() {
      //bail out if we're in a touch-based environment
      if (this.touchBasedUI) {
        return
      }

      //if the emojis are animating out after an engagement then take this early exit.
      if (this.emojisAnimationState == 6) {
        return
      }

      //As in the case of the showEmojis, we allow state 3
      //to pass through this early exit.
      if (this.emojisAnimationState == 1) {
        this.blockedAnimation = 2
        return
      }

      this.lastCalled = 'hide'

      //if we're in the rest-state 0, then we're going to set this to the waiting
      //period and start the sleep for 3 seconds
      if (this.emojisAnimationState == 5) {
        this.emojisAnimationState = 3

        if (this.waitTimer) {
          clearTimeout(this.waitTimer)
          this.waitTimer = null
        }

        this.waitTimer = setTimeout(() => {
          //if the timer ran out and we find that we're hidden,
          //then just zap all of the emojis back to their place,
          //and set the state back to 0, the fully animated-out state.
          if (!this.isFreeToPresent) {
            this.blockedAnimation = 0
            this.hideEmojisAnimate(true)
            this.emojisAnimationState = 0
            return
          }

          //If waiting period was overridden by showEmojis, but then this function
          //gets called it means that the user rolled back on during a wait,
          //but then rolled back off again. So in that case, return us to the
          //waiting period again.
          if (this.emojisAnimationState == 4) {
            if (this.lastCalled == 'show') {
              this.emojisAnimationState = 5
            } else {
              this.emojisAnimationState = 3
            }
          }

          //If we're still in the waiting period at this point, it means that the
          //user has rolled out and remained rolled out. So set the state to the
          //animating-out state and start the animation.
          if (this.emojisAnimationState == 3) {
            this.emojisAnimationState = 2
            this.blockedAnimation = 0
            this.hideEmojisAnimate()
          }
        }, this.hideEmojisMouseWait)
      }
    },

    // the show/hideEmojisAnimate just worry about animation only. Don't worry about the states,
    // those are handled elsewhere. Animation functions should be purely orthogonal to state-stuff.
    showEmojisAnimate() {
      let numberOfEmojis = this.emojisList.length
      let i = 0

      $(this.$refs.emojisContainer)
        .children()
        .forEach(el => {
          let tX =
            (100 + this.pctPaddingH) * i -
            (numberOfEmojis * (100 + this.pctPaddingH)) / 2 +
            50

          let animationOptions = {
            scale: 1,
            xPercent: tX,
            yPercent: -(100 + this.pctPaddingV),
            ease: this.customEase,
            delay: i * 0.05,
          }

          if (i == numberOfEmojis - 1) {
            animationOptions['onComplete'] = () => {
              this.resolveBlockedAnimation()
            }
          }

          TweenLite.to(el, 0.4, animationOptions)
          i++
        })
    },

    hideEmojisAnimate(zap) {
      let numberOfEmojis = this.emojisList.length
      let i = 0

      $(this.$refs.emojisContainer)
        .children()
        .forEach(el => {
          let animationOptions = {
            scale: 0,
            xPercent: 0,
            yPercent: 0,
            ease: Power2.easeInOut,
          }

          if (i == numberOfEmojis - 1) {
            animationOptions['onComplete'] = () => {
              this.resolveBlockedAnimation()
            }
          }

          if (zap) {
            TweenLite.set(el, { scale: 0, xPercent: 0, yPercent: 0 })
          } else {
            TweenLite.to(el, 0.4, animationOptions)
          }

          i++
        })
    },

    // called at the end of a show-hide animation. If the user started another animation
    // while the first one was running, we remember that one. This is basically a queue
    // that has a max-length of 1.
    resolveBlockedAnimation() {
      //If we're done showing emojis, then put us into the other rest-state.
      if (this.emojisAnimationState == 1) {
        this.emojisAnimationState = 5
      }

      //else if we're done hiding the emojis either in the context of
      //a mouse/touch or an engagement event, then set us back to the
      //initial resting state where the emojis are hidden.
      else if (
        this.emojisAnimationState == 2 ||
        this.emojisAnimationState == 6
      ) {
        this.emojisAnimationState = 0
      }

      //if there's a blocked animation, then resolve it.
      if (this.blockedAnimation == 1) {
        this.showEmojisMouse()
      } else if (this.blockedAnimation == 2) {
        this.hideEmojisMouse()
      }
    },
  },
})
